import React, { useCallback, useEffect, useState } from "react"

import { ReactComponent as CheckIcon } from "@common_assets/svg/check.svg"
import { ReactComponent as CrossIcon } from "@common_assets/svg/cross.svg"
import { ReactComponent as EyeClosedIcon } from "@common_assets/svg/eye-closed.svg"
import { ReactComponent as EyeOpenIcon } from "@common_assets/svg/eye-open.svg"
import {
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  Typography,
  useTheme,
} from "@mui/material"
import TextField from "components/form/common/field/textField"
import { useWatch } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"
import { RoutePath } from "routes/Route.types"
import { changePasswordSchema, passwordSchema } from "utils/yup"

interface IProps {
  onSubmit: () => void
  linkToResetPassword: boolean
  isLoading: boolean
}

const First = ({ onSubmit, linkToResetPassword, isLoading }: IProps) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const [showPass, setShowPass] = useState(false)
  const [showConfirmPass, setShowConfirmPass] = useState(false)

  const password = useWatch({ name: "password" })
  const confirmPassword = useWatch({ name: "confirmPassword" })

  const [isPasswordValid, setIsPasswordValid] = useState(false)
  const [isConfirmPasswordValid, setIsConfirmPasswordValid] = useState(false)

  useEffect(() => {
    if (password && password.length > 0) {
      passwordSchema
        .isValid(password)
        .then((isValid: boolean) => {
          setIsPasswordValid(isValid)
        })
        .catch(() => {
          setIsPasswordValid(false)
        })
    } else {
      setIsPasswordValid(false)
    }
  }, [password])

  useEffect(() => {
    if (confirmPassword && confirmPassword.length > 0 && password && password.length > 0) {
      changePasswordSchema
        .isValid({ password, confirmPassword })
        .then((isValid: boolean) => {
          setIsConfirmPasswordValid(isValid)
        })
        .catch(() => {
          setIsConfirmPasswordValid(false)
        })
    } else {
      setIsConfirmPasswordValid(false)
    }
  }, [password, confirmPassword])

  const showSign = useCallback(() => {
    if (!password || password.length === 0) {
      return null
    }

    if (isPasswordValid) {
      return (
        <CheckIcon
          style={{
            color: theme.palette.mockup.success50,
          }}
        />
      )
    }

    if (!isPasswordValid) {
      return (
        <CrossIcon
          style={{
            color: theme.palette.mockup.error50,
          }}
        />
      )
    }

    return null
  }, [isPasswordValid, password, theme.palette.mockup.error50, theme.palette.mockup.success50])

  const showConfirmSign = useCallback(() => {
    if (!confirmPassword || confirmPassword.length === 0) {
      return null
    }

    if (isConfirmPasswordValid) {
      return (
        <CheckIcon
          style={{
            color: theme.palette.mockup.success50,
          }}
        />
      )
    }

    if (!isConfirmPasswordValid) {
      return (
        <CrossIcon
          style={{
            color: theme.palette.mockup.error50,
          }}
        />
      )
    }

    return null
  }, [
    isConfirmPasswordValid,
    confirmPassword,
    theme.palette.mockup.error50,
    theme.palette.mockup.success50,
  ])

  return (
    <>
      <Typography
        variant="h2"
        color="mockup.neutral50"
        sx={{ textAlign: "center" }}
      >
        {t("noAuthFlow.createANewPassword")}
      </Typography>
      <Box sx={{ position: "relative" }}>
        <TextField
          name="password"
          type={showPass ? "text" : "password"}
          sx={{ mt: 4 }}
          fullWidth
          label={t("noAuthFlow.password")}
          slotProps={{
            input: {
              endAdornment: (
                <>
                  {showSign()}
                  <InputAdornment
                    component="button"
                    type="button"
                    aria-label={
                      showPass
                        ? t("noAuthFlow.hidePassword")
                        : t("noAuthFlow.showPassword")
                    }
                    sx={[
                      {
                        cursor: "pointer",
                        color: "primary.main",
                        border: "none",
                        background: "none",
                        padding: 0,
                        margin: 0,
                        fontFamily: "inherit",
                      },
                      (theme) => ({
                        "&:hover": {
                          color: theme.palette.mockup.primary80,
                        },
                      }),
                    ]}
                    position={"start"}
                    onClick={() => setShowPass(!showPass)}
                  >
                    {showPass ? <EyeOpenIcon /> : <EyeClosedIcon />}
                  </InputAdornment>
                </>
              ),
            },
          }}
        />
      </Box>
      <Box sx={{ position: "relative", mt: 2 }}>
        <TextField
          name="confirmPassword"
          type={showConfirmPass ? "text" : "password"}
          fullWidth
          label={t("noAuthFlow.confirmPassword")}
          slotProps={{
            input: {
              endAdornment: (
                <>
                  {showConfirmSign()}
                  <InputAdornment
                    component="button"
                    type="button"
                    aria-label={
                      showConfirmPass
                        ? t("noAuthFlow.hidePassword")
                        : t("noAuthFlow.showPassword")
                    }
                    sx={[
                      {
                        cursor: "pointer",
                        color: "primary.main",
                        border: "none",
                        background: "none",
                        padding: 0,
                        margin: 0,
                        fontFamily: "inherit",
                      },
                      (theme) => ({
                        "&:hover": {
                          color: theme.palette.mockup.primary80,
                        },
                      }),
                    ]}
                    position={"start"}
                    onClick={() => setShowConfirmPass(!showConfirmPass)}
                  >
                    {showConfirmPass ? <EyeOpenIcon /> : <EyeClosedIcon />}
                  </InputAdornment>
                </>
              ),
            },
          }}
        />
      </Box>
      <Button
        disabled={isLoading}
        fullWidth
        onClick={onSubmit}
        sx={{ mt: 4 }}
      >
        {isLoading && (
          <CircularProgress
            size="18px"
            sx={{
              color: "common.white",
            }}
          />
        )}{" "}
        {t("noAuthFlow.createNewPassword")}
      </Button>
      {linkToResetPassword && (
        <Box sx={{ mt: 2, textAlign: "center" }}>
          <Typography
            component={Link}
            to={{ pathname: RoutePath.RESET_PASSWORD }}
            sx={{
              cursor: "pointer",
              textDecoration: "none",
              color: "primary.main",
              "&:hover": {
                textDecoration: "underline",
              },
            }}
          >
            {t("noAuthFlow.linkToResetPassword")}
          </Typography>
        </Box>
      )}
    </>
  )
}

export default First
